import React, { useState, useEffect, useCallback } from "react";
import { Form, Icon, Image, Label, Card, Grid, Divider, Loader } from "semantic-ui-react";
import { useFormik } from "formik";
import { RegisteredPayment } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { showSuccess } from "../../../../utils/sweetalert";
import { withEmptyOption } from "../../../../utils";
import { useDropzone } from "react-dropzone";
import { initialValues, validationSchema } from "./RegisteredPaymentForm.form";
import "./RegisteredPaymentForm.scss";

const registeredPaymentController = new RegisteredPayment();

export function RegisteredPaymentForm(props) {
  const { registeredPayment, onReload, onClose } = props;
  const { accessToken } = useAuth();

  const [debtDetails, setDebtDetails] = useState(null);
  const [loadingDebt, setLoadingDebt] = useState(true);
  const [calculatedLateFee, setCalculatedLateFee] = useState(0);

  const calculateDelayDays = (dueDate) => {
    if (!dueDate) return 0;
    const today = new Date();
    const due = new Date(dueDate);
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);
    const diffTime = today - due;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) - 1;
    return diffDays > 0 ? diffDays : 0;
  };

  useEffect(() => {
    (async () => {
      try {
        setLoadingDebt(true);
        const response = await registeredPaymentController.getStudentDebts(
          registeredPayment.studentId,
          accessToken
        );
        const conceptDebt = response?.debts?.find(
          (d) => d.payment_concept_id === registeredPayment.paymentConceptId
        );
        if (conceptDebt) {
          setDebtDetails(conceptDebt);
          const delayDays = calculateDelayDays(conceptDebt.due_date);
          setCalculatedLateFee(delayDays * 1.0);
        } else {
          const delayDays = calculateDelayDays(registeredPayment?.PaymentConcept?.dueDate);
          setCalculatedLateFee(delayDays * 1.0);
        }
      } catch (error) {
        console.error("Error al obtener la deuda del estudiante", error);
      } finally {
        setLoadingDebt(false);
      }
    })();
  }, [registeredPayment, accessToken]);

  const maxTuition = registeredPayment?.pendingTuition !== undefined
    ? (parseFloat(registeredPayment.pendingTuition) || 0)
    : (debtDetails
        ? (parseFloat(debtDetails.amount_pending) || 0)
        : (parseFloat(registeredPayment?.PaymentConcept?.amount || 0))
      );

  const formik = useFormik({
    initialValues: initialValues(registeredPayment, debtDetails, calculatedLateFee),
    validationSchema: validationSchema(maxTuition),
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: async (formValue) => {
      try {
        const paymentData = {
          month: registeredPayment.month || (debtDetails?.month || new Date().getMonth() + 1),
          year: registeredPayment.year || (debtDetails?.year || new Date().getFullYear()),
          tuitionAmount: formValue.tuitionAmount,
          lateFeeAmount: formValue.lateFeeAmount,
          totalAmount: parseFloat(formValue.tuitionAmount || 0) + parseFloat(formValue.lateFeeAmount || 0),
          paymentMethod: formValue.paymentMethod,
          paymentDate: formValue.paymentDate,
          status: 1, // PENDING validation
          receiptPath: formValue.voucher, // the file
          isLateFeeWaived: formValue.isLateFeeWaived,
        };

        if (formValue.transactionCode) {
          paymentData.transactionCode = formValue.transactionCode;
        }

        await registeredPaymentController.updateRegisteredPayment(
          accessToken,
          registeredPayment.id,
          paymentData
        );
        await showSuccess(
          "¡Registro Exitoso!",
          "El abono ha sido registrado correctamente y se encuentra en espera de la aprobación por parte del administrador."
        );
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const paymentMethodOptions = [
    { key: 1, value: 1, text: "Efectivo" },
    { key: 2, value: 2, text: "Yape" },
    { key: 3, value: 3, text: "Plin" },
    { key: 4, value: 4, text: "Transferencia bancaria" },
    { key: 5, value: 5, text: "Otros" },
  ];

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      formik.setFieldValue("path", URL.createObjectURL(file));
      formik.setFieldValue("voucher", file);
    }
  }, [formik]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    onDrop,
  });

  // Watch isLateFeeWaived to zero out late fee
  useEffect(() => {
    if (formik.values.isLateFeeWaived) {
      formik.setFieldValue("lateFeeAmount", "0.00");
    } else {
      const accumulatedLateFee = debtDetails 
        ? parseFloat(debtDetails.late_fees_accumulated) 
        : parseFloat(calculatedLateFee || 0);
      const totalPaidLateFee = parseFloat(registeredPayment?.totalPaidLateFee || 0);
      const pendingLateFee = Math.max(0, (isNaN(accumulatedLateFee) ? 0 : accumulatedLateFee) - totalPaidLateFee);
      formik.setFieldValue("lateFeeAmount", pendingLateFee.toFixed(2));
    }
  }, [formik.values.isLateFeeWaived, calculatedLateFee, debtDetails, registeredPayment]);

  // Actualizar el monto total cada vez que varíen los montos individuales
  useEffect(() => {
    const tuition = parseFloat(formik.values.tuitionAmount) || 0;
    const lateFee = parseFloat(formik.values.lateFeeAmount) || 0;
    formik.setFieldValue("totalAmount", (tuition + lateFee).toFixed(2));
  }, [formik.values.tuitionAmount, formik.values.lateFeeAmount]);

  if (loadingDebt) {
    return <Loader active inline="centered" content="Cargando información de deuda..." />;
  }

  const conceptAmount = Number(registeredPayment?.tuitionAmount || registeredPayment?.PaymentConcept?.amount || 0);
  const totalPaid = Number(registeredPayment?.totalPaid || 0);
  const pendingTuitionVal = Number(registeredPayment?.pendingTuition !== undefined ? registeredPayment.pendingTuition : maxTuition);
  const lateFeeVal = Number(registeredPayment?.dynamicLateFee !== undefined ? registeredPayment.dynamicLateFee : (debtDetails?.late_fees_accumulated || calculatedLateFee || 0));
  const totalPaidLateFee = Number(registeredPayment?.totalPaidLateFee || 0);
  const pendingLateFeeVal = Math.max(0, lateFeeVal - totalPaidLateFee);
  const pendingTotalVal = pendingTuitionVal + pendingLateFeeVal;

  const getStatusText = (status) => {
    switch (status) {
      case "PENDING": return "PENDIENTE";
      case "PARTIAL": return "PAGO PARCIAL";
      case "PAID": return "PAGADO";
      case "OVERDUE": return "VENCIDO";
      case "WAIVED": return "CONDONADO";
      default: return status || "PENDIENTE";
    }
  };

  return (
    <Form className="registered-payment__form" onSubmit={formik.handleSubmit}>
      <Card fluid className="registered-payment__debt-card">
        <Card.Content>
          <Card.Header className="text-primary-header">Resumen de Cuenta por Cobrar (CXC)</Card.Header>
          <Divider />
          <Grid columns={4} divided stackable>
            <Grid.Row>
              <Grid.Column>
                <div className="stat-item">
                  <span className="stat-label">Monto de Cuota</span>
                  <span className="stat-val">S/ {conceptAmount.toFixed(2)}</span>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className="stat-item">
                  <span className="stat-label">Mora Pendiente</span>
                  <span className="stat-val text-danger">S/ {pendingLateFeeVal.toFixed(2)}</span>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className="stat-item">
                  <span className="stat-label">Monto Pagado</span>
                  <span className="stat-val text-success">S/ {totalPaid.toFixed(2)}</span>
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className="stat-item">
                  <span className="stat-label">Saldo Pendiente</span>
                  <span className="stat-val text-warning">S/ {pendingTotalVal.toFixed(2)}</span>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <div className="registered-payment__debt-meta" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Label color={debtDetails?.status === "OVERDUE" ? "red" : "blue"} horizontal>
                Estado: {getStatusText(debtDetails?.status)}
              </Label>
              {debtDetails?.due_date && (
                <span className="due-date-text" style={{ marginLeft: "1rem" }}>
                  Vence el: {new Date(debtDetails.due_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        </Card.Content>
      </Card>

      <Form.Checkbox
        toggle
        name="isLateFeeWaived"
        label="Exonerar mora"
        onChange={(e, { checked }) => {
          formik.setFieldValue("isLateFeeWaived", checked);
        }}
        checked={formik.values.isLateFeeWaived}
        style={{ marginBottom: "1.5rem" }}
      />

      <div className="registered-payment__row">
        <Form.Input
          name="transactionCode"
          label="Código de transacción"
          onChange={formik.handleChange}
          value={formik.values.transactionCode}
          error={formik.touched.transactionCode && formik.errors.transactionCode ? { content: formik.errors.transactionCode } : null}
        />
        <Form.Input
          name="paymentDate"
          type="date"
          label="Fecha de pago"
          onChange={formik.handleChange}
          value={formik.values.paymentDate}
          error={formik.touched.paymentDate && formik.errors.paymentDate ? { content: formik.errors.paymentDate } : null}
        />
      </div>

      <div className="registered-payment__row">
        <Form.Input
          name="tuitionAmount"
          type="number"
          step="0.01"
          max={maxTuition}
          label={`Monto Cuota (Máx: S/ ${maxTuition.toFixed(2)})`}
          onChange={formik.handleChange}
          value={formik.values.tuitionAmount}
          error={formik.touched.tuitionAmount && formik.errors.tuitionAmount ? { content: formik.errors.tuitionAmount } : null}
        />
        <Form.Input
          name="lateFeeAmount"
          type="number"
          step="0.01"
          readOnly={formik.values.isLateFeeWaived}
          label="Monto Mora a Pagar"
          onChange={formik.handleChange}
          value={formik.values.lateFeeAmount}
          error={formik.touched.lateFeeAmount && formik.errors.lateFeeAmount ? { content: formik.errors.lateFeeAmount } : null}
        />
        <Form.Input
          name="totalAmount"
          type="number"
          step="0.01"
          label="Monto Total a Depositar (S/)"
          readOnly
          value={formik.values.totalAmount}
          error={formik.touched.totalAmount && formik.errors.totalAmount ? { content: formik.errors.totalAmount } : null}
        />
      </div>

      <Form.Select
        className="edunova-select"
        name="paymentMethod"
        label="Método de pago"
        placeholder="Selecciona un método de pago"
        clearable
        options={withEmptyOption(paymentMethodOptions, "Selecciona un método de pago")}
        onChange={(e, { value }) => {
          formik.setFieldTouched("paymentMethod", true);
          formik.setFieldValue("paymentMethod", value);
        }}
        value={formik.values.paymentMethod}
        error={formik.touched.paymentMethod && formik.errors.paymentMethod ? { content: formik.errors.paymentMethod } : null}
      />

      <div className="field">
        <label style={{ fontWeight: "bold", display: "block", marginBottom: "0.5rem" }}>Comprobante de Pago (Voucher)</label>
        <div
          className={`registered-payment__image-uploader ${isDragActive ? "active" : ""} ${formik.errors.voucher ? "error" : ""}`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          {formik.values.path ? (
            <div className="uploaded-preview">
              <Image size="small" src={formik.values.path} rounded centered />
              <div className="change-img-overlay">
                <Icon name="exchange" size="large" />
                <p>Cambiar imagen</p>
              </div>
            </div>
          ) : (
            <div className="uploader-placeholder">
              <Icon name="cloud upload" size="huge" />
              <p>Arrastra tu comprobante aquí, o haz clic para seleccionarlo</p>
              <span className="file-types">Formatos válidos: JPG, PNG</span>
            </div>
          )}
        </div>
        {formik.touched.voucher && formik.errors.voucher && (
          <Label prompt className="registered-payment__label-error" basic pointing>
            {formik.errors.voucher}
          </Label>
        )}
      </div>

      <Form.Button
        className="registered-payment__button"
        type="submit"
        primary
        fluid
        loading={formik.isSubmitting}
      >
        Enviar Reporte de Pago
      </Form.Button>
    </Form>
  );
}
